import React from 'react';

import { Group, Stack } from '@mantine/core';
import DiscountCodeInput from 'components/Discounts/DiscountCodeInput';
import dayjs from 'dayjs';
import useSalesPortalKiloClassFromUrlParam from 'pages/sales-portal/useKiloClassFromParam';

import DetailField from './DetailField';
import OrderSummary from './OrderSummary';
import PaymentMethod from './PaymentMethod';
import useConfirmPurchase from './useConfirmPurchase';
import { useCheckout } from '../../CheckoutContext';
import { chargesFirstInvoiceImmediately, getCheckoutStartDate } from '../../utils';
import FinishingUpOverlay from '../FinishingUp';

// Context-connected wrapper for the Order Summary sidebar. Keeps OrderSummary
// itself presentational (and Storybook-renderable).
const OrderSummaryPanel = (): JSX.Element => {
  const {
    form,
    packageData,
    billingPlan,
    appliedDiscount,
    setAppliedDiscount,
    allStepsCompleted,
    isStepCompleted,
  } = useCheckout();
  const { kiloClassId, data: kiloClassData } = useSalesPortalKiloClassFromUrlParam();
  const { confirmPurchase, isSubmitting, is3DSAuthenticating } = useConfirmPurchase();

  const firstInstallmentPriceCents =
    billingPlan.installments?.find(installment => installment.installmentNumber === 1)?.price ?? 0;

  const effectiveStartDate = getCheckoutStartDate(
    packageData,
    kiloClassId ? kiloClassData?.data?.startDate : null,
  );
  const showImmediateChargeWarning = chargesFirstInvoiceImmediately(packageData, !!kiloClassData?.data);

  const formattedStartDate = dayjs(effectiveStartDate).format('MMMM D, YYYY');
  const athleteFullName =
    `${form.values.athleteInformation.firstName} ${form.values.athleteInformation.lastName}`.trim();

  const detailsSlot = (
    <Group position='apart' align='start' data-cy='orderSummaryDetails'>
      <Stack spacing='xs'>
        <DetailField label='Package Start Date'>{formattedStartDate}</DetailField>
        <DetailField label='Billing Date'>{formattedStartDate}</DetailField>
        {isStepCompleted('athleteInformation') && athleteFullName && (
          <DetailField label='Athlete'>{athleteFullName}</DetailField>
        )}
      </Stack>
      <Stack spacing='xs'>
        <DetailField label='Auto Renew'>{packageData.autoRenew ? 'On' : 'Off'}</DetailField>
        <DetailField label='Auto Pay'>
          {billingPlan.salesPortalRequiresPaymentMethod ? 'On' : 'Off'}
        </DetailField>
        {isStepCompleted('payment') && billingPlan.salesPortalRequiresPaymentMethod && <PaymentMethod />}
      </Stack>
    </Group>
  );

  const discountSlot = (
    <DiscountCodeInput
      salesPortalGym={packageData.gym.gymId}
      productId={packageData.product}
      value={appliedDiscount}
      onChange={setAppliedDiscount}
      packageTotalCents={firstInstallmentPriceCents}
    />
  );

  return (
    <>
      {/* Spec Item 1 "Finishing up…": covers the page while the checkout POST is in flight
          (not during 3DS, whose bank prompt must stay reachable). */}
      <FinishingUpOverlay visible={isSubmitting} />
      <OrderSummary
        gym={packageData.gym}
        packageData={packageData}
        billingPlan={billingPlan}
        taxRate={packageData?.taxRate?.rate || 0}
        appliedDiscount={appliedDiscount}
        discountSlot={discountSlot}
        detailsSlot={detailsSlot}
        showImmediateChargeWarning={showImmediateChargeWarning}
        allStepsCompleted={allStepsCompleted}
        isSubmitting={isSubmitting}
        is3DSAuthenticating={is3DSAuthenticating}
        onConfirm={confirmPurchase}
      />
    </>
  );
};

export default OrderSummaryPanel;
